import { useCallback, useEffect, useState } from 'react';
import { FormInput, FormProvider, useAlert, useForm } from '@hilma/netina2all';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { provide, useAsyncEffect } from '@hilma/tools';
import Skeleton from '@material-ui/lab/Skeleton';
import Tooltip from '@material-ui/core/Tooltip';
import { useCopyToClipboard } from 'react-use';
import Button from '@material-ui/core/Button';
import { Redirect } from 'react-router-dom';
import clsx from 'clsx';

import { useProfileFreeSaleLink, useFreeSaleStatus, useGetFreeSale, useI18n, useTranslate, useStationStatus } from '../../../hooks';
import { freeSaleTexts } from '../../../constants';
import { FreeSaleStatus } from '../../../types';

import ProfileTitle from '../ProfileTitle';

interface FreeSaleProps /* extends RouteComponentProps */ {
	isMobile?: boolean;
}

const FreeSale: React.FC<FreeSaleProps> = ({ isMobile }) => {
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const [error, setError] = useState(false);

	const classes = useStyles();

	const freeSale = useI18n(i18n => i18n.freeSale);
	const generalSystem = useI18n(i18n => i18n.errText.generalSystem);
	const fs = useI18n(i18n => i18n.sideBar.sm.fs);
	const translate = useTranslate();

	const link = useProfileFreeSaleLink();
	const status = useFreeSaleStatus();
	const { isOpenOrClose } = useStationStatus();
	const getFreeSale = useGetFreeSale();

	const form = useForm();

	const alert = useAlert();

	const [, copyToClipboard] = useCopyToClipboard();

	const onCopyClick = useCallback(() => {
		copyToClipboard(link);
		setTooltipOpen(true);
	}, [link, copyToClipboard]);

	useAsyncEffect(async () => {
		try {
			setError(false);
			await getFreeSale();
		} catch (error) {
			console.error(error);
			alert(generalSystem);
			setError(true);
		}
	}, []);

	useEffect(() => {
		if (!tooltipOpen) return;

		const timeoutId = setTimeout(() => {
			setTooltipOpen(false);
		}, 2000);

		return () => {
			clearTimeout(timeoutId);
		}
	}, [tooltipOpen]);

	useEffect(() => {
		form.setFieldValue('link', link);
	}, [link]);


	if (!isOpenOrClose) return (
		<Redirect to="/" />
	)

	if (!status && !error) return (
		<div>
			<Skeleton variant="text" className={clsx(classes.skeleton, classes.text)} width={150} />
			<Skeleton variant="text" className={classes.text} width={300} />
		</div>
	);

	return (
		<div>
			{!isMobile && <ProfileTitle title={fs} />}
			{status && <div className={classes.text}>{translate(freeSaleTexts[status])}</div>}
			{status === FreeSaleStatus.Open && (
				<div className={classes.inputContainer}>
					<FormInput
						id="link"
						description={freeSale.linkTo}
						readOnly
						classes={{
							errorContainer: classes.errorContainer
						}}
						inputProps={{
							size: link.length - 4
						}}
						dir="ltr"
					/>
					<Tooltip
						PopperProps={{
							disablePortal: true,
						}}
						open={tooltipOpen}
						disableFocusListener
						disableHoverListener
						disableTouchListener
						title={freeSale.copied}
					>
						<Button
							color="primary"
							onClick={onCopyClick}
							classes={{
								root: classes.button,
								label: classes.buttonLabel
							}}
						>
							{freeSale.copy}
						</Button>
					</Tooltip>
				</div>
			)}
		</div>
	);
}

export default provide(
	[FormProvider, { initialValues: { link: '' }, rules: { link: {} as any } }]
)(FreeSale);

export const useStyles = makeStyles(theme => ({
	skeleton: {
		marginBottom: '0 !important'
	},

	text: {
		fontSize: 18.5,
		marginBottom: 24,
		fontFamily: 'Assistant-Regular',
		color: theme.palette.primary.main,
		textAlign: 'left',
		[theme.breakpoints.up(1600)]: {
			fontSize: 25,
			marginBottom: 33
		}
	},

	inputContainer: {
		display: 'flex',
		alignItems: 'flex-end',
		flexWrap: 'wrap',
		[theme.breakpoints.down(800)]: {
			justifyContent: 'flex-end',
			padding: '0 12px'
		}
	},

	errorContainer: {
		display: 'none'
	},

	button: {
		marginLeft: 15,
		paddingLeft: 11,
		paddingRight: 11,

		[theme.breakpoints.up(1600)]: {
			marginLeft: 20,
			paddingLeft: 15,
			paddingRight: 15,
		},

		[theme.breakpoints.down(1000)]: {
			marginLeft: 0,
			marginTop: 5
		}
	},

	buttonLabel: {
		fontSize: 18.5,
		textTransform: 'none',
		fontFamily: 'Assistant-SemiBold',
		[theme.breakpoints.up(1600)]: {
			fontSize: 25,
		},
		[theme.breakpoints.down(800)]: {
			fontFamily: 'Assistant-Regular',
			textDecoration: 'underline'
		}
	}
}));